import * as faceapi from "@vladmandic/face-api";

let modelsPromise = null;

/**
 * Carrega todos os modelos neurais do face-api servidos em /models.
 */
export const loadFaceModels = async () => {
  if (modelsPromise) {
    return modelsPromise;
  }

  modelsPromise = (async () => {
    try {
      const MODEL_URL = "/models";
      await Promise.all([
        faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);

      return true;
    } catch (error) {

      modelsPromise = null;
      throw error;
    }
  })();

  return modelsPromise;
};

/**
 * Converte arquivo em elemento HTMLImageElement
 */
export const fileToImageElement = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.crossOrigin = "anonymous";
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(objectUrl);
      reject(err);
    };
    img.src = objectUrl;
  });
};

/**
 * Distância Euclidiana entre dois descritores de 128 dimensões.
 */
export const euclideanDistance = (a, b) => {
  if (!a || !b || a.length !== b.length) return 999;
  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    const diff = a[i] - b[i];
    sum += diff * diff;
  }
  return Math.sqrt(sum);
};

/**
 * Converte a distância euclidiana em uma porcentagem de confiança calibrada.
 * - <= 0.35 -> 95% a 99% (Quase idêntico)
 * - 0.36 a 0.56 -> 80% a 94% (Alta Certeza)
 * - 0.57 a 0.62 -> 65% a 79% (Média Certeza / Sugestão)
 * - > 0.62 -> < 60% (Inconclusivo / Outra pessoa)
 */
export const distanceToConfidence = (dist) => {
  if (dist <= 0.35) return Math.min(99, Math.round(95 + (0.35 - dist) * 10));
  if (dist <= 0.56) return Math.round(80 + ((0.56 - dist) / 0.21) * 14);
  if (dist <= 0.62) return Math.round(65 + ((0.62 - dist) / 0.06) * 14);
  if (dist <= 0.68) return Math.round(50 + ((0.68 - dist) / 0.06) * 14);
  return Math.max(10, Math.round(45 - (dist - 0.68) * 40));
};

/**
 * Redimensiona proporcionalmente imagens de alta resolução (4K/6K)
 * para um tamanho ideal (max 1600px), acelerando o processamento
 * e preservando detalhes nítidos dos rostos.
 */
export const prepareImageForDetection = async (imageInput, maxDimension = 1600) => {
  let img = imageInput;
  if (imageInput instanceof File || imageInput instanceof Blob) {
    img = await fileToImageElement(imageInput);
  }

  const origWidth = img.naturalWidth || img.width;
  const origHeight = img.naturalHeight || img.height;

  if (origWidth <= maxDimension && origHeight <= maxDimension) {
    return {
      element: img,
      origImg: img,
      scale: 1,
      origWidth,
      origHeight,
    };
  }

  const scale = Math.min(maxDimension / origWidth, maxDimension / origHeight);
  const targetWidth = Math.round(origWidth * scale);
  const targetHeight = Math.round(origHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  return {
    element: canvas,
    origImg: img,
    scale,
    origWidth,
    origHeight,
  };
};

/**
 * Recorta a região do rosto detectado com uma margem de segurança
 * e gera um DataURL para exibir a miniatura do rosto na interface.
 */
export const cropFace = (sourceImg, box) => {
  try {
    const canvas = document.createElement("canvas");
    const margin = 0.25;
    const imgW = sourceImg.naturalWidth || sourceImg.width;
    const imgH = sourceImg.naturalHeight || sourceImg.height;

    const cropX = Math.max(0, box.x - box.width * margin);
    const cropY = Math.max(0, box.y - box.height * margin);
    const cropW = Math.min(imgW - cropX, box.width * (1 + margin * 2));
    const cropH = Math.min(imgH - cropY, box.height * (1 + margin * 2));

    canvas.width = Math.min(180, Math.round(cropW));
    canvas.height = Math.min(180, Math.round(cropH));

    const ctx = canvas.getContext("2d");
    ctx.drawImage(sourceImg, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.85);
  } catch (err) {
    console.warn("[FaceAPI] Erro ao recortar rosto:", err);
    return null;
  }
};

/**
 * Extrai o descritor facial de uma foto de referência no cadastro do atleta.
 * Exige rosto nítido e bem definido (minConfidence: 0.50).
 */
export const extractReferenceFaceDescriptor = async (imageInput) => {
  await loadFaceModels();

  const { element, origImg, scale } = await prepareImageForDetection(imageInput, 1600);

  // Exige confiança mínima de 0.50 para foto de referência nítida
  let detections = await faceapi
    .detectAllFaces(element, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.50 }))
    .withFaceLandmarks()
    .withFaceDescriptors();

  if (!detections || detections.length === 0) {
    detections = await faceapi
      .detectAllFaces(element, new faceapi.TinyFaceDetectorOptions({ inputSize: 512, scoreThreshold: 0.45 }))
      .withFaceLandmarks()
      .withFaceDescriptors();
  }

  if (!detections || detections.length === 0) {
    return {
      success: false,
      error: "Nenhum rosto foi identificado na foto. Certifique-se de que o rosto do atleta está bem iluminado e visível.",
    };
  }

  if (detections.length > 1) {
    return {
      success: false,
      error: `Foram detectados ${detections.length} rostos na foto. Por favor envie uma foto individual apenas deste atleta.`,
    };
  }

  const detection = detections[0];
  const rawBox = detection.detection.box;
  const originalBox = {
    x: Math.round(rawBox.x / scale),
    y: Math.round(rawBox.y / scale),
    width: Math.round(rawBox.width / scale),
    height: Math.round(rawBox.height / scale),
  };

  const faceCropUrl = cropFace(origImg, originalBox);

  return {
    success: true,
    descriptor: Array.from(detection.descriptor),
    box: originalBox,
    faceCropUrl,
  };
};

/**
 * Reconhece rostos em uma imagem de evento, compara com todos os atletas cadastrados
 * e filtra rostos borrados ou com baixa certeza biométrica.
 */
export const recognizeFacesInImage = async (imageInput, atletasComFotos = []) => {
  await loadFaceModels();

  const { element, origImg, scale, origWidth, origHeight } = await prepareImageForDetection(
    imageInput,
    1600
  );



  // 1. Detecção com SSD MobileNet - minConfidence 0.45 para evitar detectar manchas/borrões como rostos
  let detections = await faceapi
    .detectAllFaces(element, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.45 }))
    .withFaceLandmarks()
    .withFaceDescriptors();

  // 2. Se não encontrou, tenta TinyFaceDetector com score seguro (0.40)
  if (!detections || detections.length === 0) {

    detections = await faceapi
      .detectAllFaces(element, new faceapi.TinyFaceDetectorOptions({ inputSize: 512, scoreThreshold: 0.40 }))
      .withFaceLandmarks()
      .withFaceDescriptors();
  }



  if (!detections || detections.length === 0) {
    return {
      totalFaces: 0,
      matches: [],
      facesDetails: [],
      unknownFaces: 0,
    };
  }

  const facesDetails = [];
  const matchesMap = new Map();
  let unknownFaces = 0;
  let validFacesCount = 0;

  for (let i = 0; i < detections.length; i++) {
    const det = detections[i];
    const rawBox = det.detection.box;
    const box = {
      x: Math.round(rawBox.x / scale),
      y: Math.round(rawBox.y / scale),
      width: Math.round(rawBox.width / scale),
      height: Math.round(rawBox.height / scale),
    };

    // Filtro anti-borrão e tamanho mínimo:
    // Rostos menores que 48x48px na foto original não possuem detalhes suficientes
    // para reconhecimento facial confiável em esportes e geram falsos positivos.
    if (box.width < 48 || box.height < 48) {

      continue;
    }

    validFacesCount++;

    // Gera recorte visual do rosto para a interface
    const faceCropUrl = cropFace(origImg, box);
    const queryDescriptor = Array.from(det.descriptor);

    // Compara com todas as referências dos atletas cadastrados
    const rankedAthletes = [];

    for (const atleta of atletasComFotos) {
      if (!Array.isArray(atleta.fotosReferencia)) continue;

      let minDistance = 999;
      for (const foto of atleta.fotosReferencia) {
        if (Array.isArray(foto.descriptor) && foto.descriptor.length === 128) {
          const d = euclideanDistance(queryDescriptor, foto.descriptor);
          if (d < minDistance) {
            minDistance = d;
          }
        }
      }

      if (minDistance < 999) {
        rankedAthletes.push({
          atletaId: atleta._id,
          atletaNome: atleta.nome,
          atleta,
          distance: minDistance,
          confidence: distanceToConfidence(minDistance),
        });
      }
    }

    // Guarda candidatos deste rosto
    facesDetails.push({
      faceIndex: validFacesCount - 1,
      box,
      faceCropUrl,
      rankedAthletes,
      bestMatch: null,
      topCandidates: [],
      status: "unknown",
      isAmbiguous: false,
    });
  }

  // -------------------------------------------------------------
  // REGRA DE OURO 1-PARA-1: Uma mesma pessoa não pode estar 2x na mesma foto!
  // Se múltiplos rostos forem parecidos com a mesma atleta,
  // atribuímos a atleta EXCLUSIVAMENTE ao rosto de maior taxa de aproximação
  // (menor distância euclidiana).
  // -------------------------------------------------------------
  const allPairings = [];

  for (let fIdx = 0; fIdx < facesDetails.length; fIdx++) {
    const f = facesDetails[fIdx];
    for (const cand of f.rankedAthletes) {
      // Limiar máximo para qualquer consideração: 0.60
      if (cand.distance <= 0.60) {
        allPairings.push({
          faceIndex: fIdx,
          atletaId: cand.atletaId,
          atletaNome: cand.atletaNome,
          atleta: cand.atleta,
          distance: cand.distance,
          confidence: cand.confidence,
        });
      }
    }
  }

  // Ordena todos os pares pela menor distância (maior aproximação)
  allPairings.sort((a, b) => a.distance - b.distance);

  const assignedFaceIndices = new Set();
  const assignedAtletaIds = new Set();

  for (const pair of allPairings) {
    if (!assignedFaceIndices.has(pair.faceIndex) && !assignedAtletaIds.has(pair.atletaId)) {
      assignedFaceIndices.add(pair.faceIndex);
      assignedAtletaIds.add(pair.atletaId);

      const face = facesDetails[pair.faceIndex];
      // Alta certeza (<= 0.54): confirmação sólida
      // Média certeza (0.55 a 0.60): sugestão para desambiguação
      const isSolidMatch = pair.distance <= 0.54;

      face.bestMatch = pair;
      face.status = isSolidMatch ? "confirmed" : "suggested";
      face.topCandidates = face.rankedAthletes
        .filter((c) => c.distance <= 0.60)
        .slice(0, 3);

      matchesMap.set(pair.atletaId, {
        atletaId: pair.atletaId,
        atletaNome: pair.atletaNome,
        atleta: pair.atleta,
        distance: pair.distance,
        confidence: pair.confidence,
        isSolidMatch,
        faceCropUrl: face.faceCropUrl,
        box: face.box,
      });

    }
  }

  // Rostos não atribuídos (ou cuja atleta candidata já pertencia a outro rosto mais nítido/próximo)
  for (let fIdx = 0; fIdx < facesDetails.length; fIdx++) {
    const face = facesDetails[fIdx];
    if (!assignedFaceIndices.has(fIdx)) {
      face.bestMatch = null;
      face.status = "unknown";
      face.topCandidates = face.rankedAthletes.slice(0, 3);
      unknownFaces++;

    }
  }

  const matches = Array.from(matchesMap.values());

  return {
    totalFaces: validFacesCount,
    matches,
    facesDetails,
    unknownFaces,
  };
};
